import { FRAME_MS, MOTION_FRAMES, TICK_MS, healthShown, type RegenerationRate, type Resource } from "odyssey-shared";
import { healthFill, healthLow } from "../../ui/kit/health.ts";
import { cutToFit } from "../../ui/kit/fit.ts";
import { GLYPH_SIZES, KIT_METRICS, centreOffset } from "../../ui/kit/metrics.ts";
import { pixelTextWidth } from "../../ui/kit/pixel-text.ts";
import { smallDigits, type SmallDigits } from "../../ui/kit/small-digits.ts";
import { VITALS, VITALS_CARD } from "./slots.ts";
import type { HudVitals } from "./store.ts";
import { say } from "../../words.ts";

/**
 * What the player card draws from the HUD store's values (ADR-061, amendment "the player card"), kept apart from
 * the component so the rules are tested without React: the room the fixed-width header leaves the name and the name
 * as it fits that room, each bar's fill (the kit's health rule, `healthFill`, at least one column for anything above
 * 0, on every bar of the card), whether the health bar flashes (`healthLow`), the start that keeps the card's CSS
 * flashes in the phase `lowHealthLit` reads, the health value's text and place in its bar, the health bar's regeneration segment, the part
 * of the health bar a maximum-health penalty locks (ADR-079's amendment of 2026-10-03 for P2.2-T16), whether the
 * in-combat mark shows, and when the message line holds the Stamina bar's empty line (ADR-116 decision 15).
 */

/**
 * A bar as the card draws it: the fill's width in whole logical pixels, and the value and maximum for assistive
 * technology (`now` null before the first `vitals`).
 */
export interface VitalBar {
  readonly fill: number;
  readonly now: number | null;
  readonly max: number;
}

/**
 * The health bar: a bar that can be low, with its value's text (null before the first `vitals`), the value drawn in
 * the small digits when the viewer shows health numbers (null otherwise), the drawn value's left inside the bar, and
 * the width of the regeneration segment after the fill in whole logical pixels (`regenerationPending`), and the width
 * of the locked segment at the bar's end, the part a maximum-health penalty removes (0 for none). Under a penalty `max`
 * and the value's maximum are the full, unpenalised maximum (`healthShown`).
 */
export interface HealthBar extends VitalBar {
  readonly low: boolean;
  readonly value: string | null;
  readonly digits: SmallDigits | null;
  readonly valueLeft: number;
  readonly pending: number;
  readonly locked: number;
}

/**
 * What the card knows of a maximum-health penalty: the health pool of the character's level (null before its level is
 * known) and its own effects' summed `maxHealth` modifier in whole percents (`frameMaxHealthPercent`).
 */
export interface HealthPenalty {
  readonly pool: number | null;
  readonly percent: number;
}

/**
 * No penalty: the served maximum is the full one.
 */
export const NO_HEALTH_PENALTY: HealthPenalty = Object.freeze({ pool: null, percent: 0 });

/**
 * Every bar on the card, and whether the header shows the in-combat mark (the `vitals` state bit).
 */
export interface VitalsView {
  readonly health: HealthBar;
  readonly stamina: VitalBar;
  readonly focus: VitalBar;
  readonly inCombat: boolean;
}

/**
 * Ticks in a second.
 */
const TICKS_PER_SECOND = 1000 / TICK_MS;

/**
 * What a resource regains in the next second at a rate, in whole units or fractions of one, never more than it lacks:
 * the amount the design's regeneration segment shows after the fill (P2.2-T1). The page draws it from the rules and
 * the in-combat bit the server sent, and never moves the fill itself, so the bar still shows only the server's value.
 *
 * @param now The value.
 * @param max Its maximum.
 * @param rate The rate the server runs while the character is in combat or out of it.
 * @returns The units, 0 for a full or empty resource.
 * @example
 * regenerationPending(50, 100, { amount: 1, unit: "percent", everyTicks: 4 });
 * // 5
 */
export function regenerationPending(now: number, max: number, rate: RegenerationRate): number {
  if (!(now > 0) || !(max > now) || !(rate.everyTicks > 0)) {
    return 0;
  }
  const step = rate.unit === "percent" ? (rate.amount * max) / 100 : rate.amount;
  return Math.min(max - now, (step * TICKS_PER_SECOND) / rate.everyTicks);
}

/**
 * The low flash's period: the motion table's `blink`, 12 frames of 40 ms.
 */
export const LOW_FLASH_MS = MOTION_FRAMES.blink * FRAME_MS;

/**
 * The names of the CSS animations the card flashes with (`hud.css`): the low health fill and the flag countdown's
 * current mark, and the countdown's next mark in the other half of the beat.
 */
export const CARD_FLASHES: ReadonlySet<string> = new Set(["ot-hud-low-flash", "ot-hud-flag-swap"]);

/**
 * What `holdFlashPhase` reads and writes of an animation on an element: a CSS animation's name, which a transition
 * lacks, and its start on the document timeline, null while it waits for its first frame.
 */
export interface FlashAnimation {
  readonly animationName?: string;
  startTime: unknown;
}

/**
 * Puts the card's flashes among an element's animations on the page clock: each starts at the document timeline's
 * origin, which is `performance.now()`'s, so its time is the clock's and a flash lit for the first half of each period
 * is lit exactly when `lowHealthLit` reads lit, and every low bar, in the DOM or on the canvas, flashes together. A
 * CSS animation otherwise starts at the first frame after the style that made it, a frame or a stalled frame after the
 * element turned low, and keeps that lateness as its phase for as long as it runs. Any other animation is left alone.
 *
 * @param animations The element's animations (`Element.getAnimations`).
 * @example
 * holdFlashPhase(fill.getAnimations());
 */
export function holdFlashPhase(animations: Iterable<FlashAnimation>): void {
  for (const animation of animations) {
    if (animation.animationName !== undefined && CARD_FLASHES.has(animation.animationName)) {
      animation.startTime = 0;
    }
  }
}

/**
 * The level as the header writes it.
 *
 * @param level The level, or null before the character's entity arrives.
 * @returns "Lv" and the number, or null for no level.
 * @example
 * levelText(1);
 * // "Lv 1"
 */
export function levelText(level: number | null): string | null {
  return level === null ? null : `${say("hud.vitals.level")} ${level}`;
}

/**
 * The width the header keeps for the in-combat mark, whether or not the mark shows: the kit's zap glyph. With
 * `VITALS.nameGap` before it, it is held after the name at all times, so entering or leaving combat moves nothing.
 */
export const COMBAT_SLOT = GLYPH_SIZES.zap;

/**
 * The room the header leaves the name at the card's fixed content width (`VITALS_CARD.width`): what is left after the
 * mark's box, the gap before the name, the in-combat mark's reserved slot with its gap, and the level with its gap
 * when one shows.
 *
 * @param level The level's text, or null when none shows.
 * @returns The room in logical pixels, never below 0.
 * @example
 * nameRoom("Lv 99");
 * // 72
 */
export function nameRoom(level: string | null): number {
  const levelPart = level === null ? 0 : VITALS.levelGap + pixelTextWidth(level);
  return Math.max(0, VITALS_CARD.width - KIT_METRICS.flag[VITALS.flagSize].box - VITALS.nameGap - VITALS.nameGap - COMBAT_SLOT - levelPart);
}

/**
 * A name as it fits a room in the pixel face: whole when its advances fit, otherwise its longest start that fits with
 * U+2026 after it, cut between glyphs (code points) and measured by the face's advances (`pixelTextWidth`), which are
 * the widths the DOM sets the name at. A room too narrow for any glyph before the ellipsis gives the ellipsis alone.
 *
 * @param name The name.
 * @param room The room in logical pixels.
 * @returns The name to draw.
 * @throws RangeError when the name holds a character the pixel face has no glyph for.
 * @example
 * fitName("MWMWMWMWMWMW", 72);
 * // "MWMWMWMW…"
 */
export function fitName(name: string, room: number): string {
  return cutToFit(name, (candidate) => pixelTextWidth(candidate) <= room);
}

/**
 * One bar's fill and values.
 *
 * @param now The value, or null before the first `vitals`.
 * @param max Its maximum.
 * @param inner The bar's inside.
 * @returns The bar.
 */
function bar(now: number | null, max: number, inner: number): VitalBar {
  return { fill: now === null ? 0 : healthFill(now, max, inner), now, max };
}

/**
 * Every bar on the card, at a content width, with the health bar's regeneration segment: the next second at the
 * rate for the in-combat bit, from the fill's end to where that health would fill, none while dead. Under a
 * maximum-health penalty the health bar is drawn against the full maximum (`healthShown`): its value reads the current
 * health over it ("98/100"), and the locked segment at its end, at least one pixel, is the part the penalty removes,
 * which neither the fill nor the regeneration segment enters. The low flash reads the served maximum.
 *
 * @param vitals The player's own values, or null before the first `vitals`.
 * @param width The card's content width (`VITALS_CARD.width`).
 * @param numbers Whether the viewer shows health numbers (the setting, on by default).
 * @param health Health's regeneration in the page's rules.
 * @param penalty The level's pool and the effects' maximum-health modifier, none by default.
 * @returns The bars.
 * @example
 * vitalsView({ ...full, health: 98, maxHealth: 98 }, 120, true, rules.resources.health, { pool: 100, percent: -2 }).health.value;
 * // "98/100"
 */
export function vitalsView(vitals: HudVitals | null, width: number, numbers: boolean, health: Resource, penalty: HealthPenalty = NO_HEALTH_PENALTY): VitalsView {
  const inner = width - 2 * VITALS.edge;
  if (vitals === null) {
    const empty = bar(null, 0, inner);
    return { health: { ...empty, low: false, value: null, digits: null, valueLeft: 0, pending: 0, locked: 0 }, stamina: empty, focus: empty, inCombat: false };
  }
  const shown = healthShown(vitals.health, vitals.maxHealth, penalty.pool, penalty.percent);
  const locked = shown.locked === 0 ? 0 : Math.max(1, inner - healthFill(vitals.maxHealth, shown.full, inner));
  const open = inner - locked;
  const value = `${shown.current}/${shown.full}`;
  const digits = numbers ? smallDigits(value) : null;
  const fill = Math.min(open, healthFill(shown.current, shown.full, inner));
  const rate = vitals.inCombat ? health.regeneration.inCombat : health.regeneration.outOfCombat;
  const regained = vitals.dead ? 0 : regenerationPending(shown.current, vitals.maxHealth, rate);
  const pending = regained === 0 ? 0 : Math.max(0, Math.min(open, healthFill(shown.current + regained, shown.full, inner)) - fill);
  return {
    health: { fill, now: shown.current, max: shown.full, low: healthLow(shown.current, vitals.maxHealth), value, digits, valueLeft: digits === null ? 0 : Math.max(0, centreOffset(inner, digits.width)), pending, locked },
    stamina: bar(vitals.stamina, vitals.maxStamina, inner),
    focus: bar(vitals.focus, vitals.maxFocus, inner),
    inCombat: vitals.inCombat && !vitals.dead,
  };
}

/**
 * Whether the player's own Stamina is empty, which is when the message line holds the Stamina bar's empty line
 * (ADR-116 decision 15, the design's Vitals `empty` state): the last `vitals` say 0 Stamina and the character is
 * alive. It reads the page's own vitals and nothing of any other character. A dead character is not told: the death
 * screen holds the page and the respawn fills the bar.
 *
 * @param vitals The player's own vitals as the last `vitals` carried them, null before the first.
 * @returns True while the line shows.
 */
export function staminaEmpty(vitals: HudVitals | null): boolean {
  return vitals !== null && !vitals.dead && vitals.stamina === 0;
}

/**
 * What the message line shows: a reply for its time, and under it, while the player's own Stamina is empty, the
 * Stamina bar's empty line. So a reply said at an empty bar, a weak basic blow's line among them (ADR-117 decision 9),
 * shows over the line and the line returns when the reply's time runs out; nothing is queued and no clock is kept for
 * it.
 *
 * @param message The reply on the message line, or null for none.
 * @param vitals The player's own vitals, null before the first.
 * @returns The line to draw, or null for none.
 */
export function messageShown(message: string | null, vitals: HudVitals | null): string | null {
  return message ?? (staminaEmpty(vitals) ? say("hud.line.noStamina") : null);
}
